// Cash-out review. The list is a queue (held ones first, as tabs); a row opens a review drawer with
// everything needed to decide in one place: what is being bought, for whom, Claude's call with its
// evidence in words, the risk signals, delivery state and history. Decisions go through dialogs
// that separate the internal reason from what the user is told.
import { Fragment, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Bot,
  Check,
  ChevronRight,
  Gift,
  Hand,
  RotateCcw,
  ShieldAlert,
  ShieldCheck,
  Truck,
  X,
} from 'lucide-react';
import { api } from '../lib/api';
import { useCan } from '../lib/staff';
import { useAssistantContext } from '../lib/assistant';
import { CoinGlyph } from '../components/Coin';
import { ID_CHECK_VALID_MS } from '../components/IdCheck';
import {
  Card,
  confirmAction,
  CopyButton,
  Drawer,
  EmptyState,
  ErrorNote,
  fmtMoney,
  fmtNum,
  fmtUsd,
  KV,
  Mono,
  PageHeader,
  Pill,
  RiskMeter,
  run,
  ScrollX,
  SearchInput,
  SkeletonRows,
  StatusPill,
  Tabs,
  TimeAgo,
  useDebounced,
  UserChip,
  actorLabel,
  ViewOnly,
} from '../components/ui';
import { describeRef, FEATURES, ruleLabel, type RiskFeatures } from '../lib/risk';

interface Decision {
  id: string;
  action: string;
  actor: string;
  mode: string;
  confidence: number | null;
  reasoning: string | null;
  evidence: { ref: string; note?: string }[] | null;
  outcome: string | null;
  createdAt: number;
}
interface Item {
  productId: string;
  skuId: string;
  amount: number;
  productName: string;
  imageUrl: string | null;
  productCurrency: string | null;
  fields?: { key: string; label: string }[];
}
interface Row {
  id: string;
  userId: string;
  username: string;
  /** null for a deleted account. */
  email: string | null;
  userStatus: string;
  coins: number;
  amountMinor: number;
  costMinor: number | null;
  status: string;
  riskScore: number | null;
  item: Item | null;
  externalRef: string | null;
  createdAt: number;
  updatedAt: number;
  latestDecision: Decision | null;
}

const TABS = {
  hold: { label: 'Needs you', statuses: 'on_hold' },
  progress: {
    label: 'In progress',
    statuses: 'requested,risk_review,kyc_pending,approved,processing',
  },
  paid: { label: 'Paid', statuses: 'paid' },
  closed: { label: 'Rejected & failed', statuses: 'rejected,failed,refunded' },
  all: { label: 'All', statuses: '' },
} as const;
type TabKey = keyof typeof TABS;

const OPEN = ['requested', 'risk_review', 'on_hold', 'kyc_pending'];

const cardName = (w: { item: Item | null; amountMinor: number }) =>
  w.item
    ? `${fmtMoney(w.item.amount, w.item.productCurrency ?? 'USD')} ${w.item.productName}`
    : `${fmtUsd(w.amountMinor / 100)} gift card`;

export function Withdrawals() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as TabKey) in TABS ? (params.get('tab') as TabKey) : 'hold';
  const selected = params.get('id');
  const [q, setQ] = useState('');
  const search = useDebounced(q.trim());
  const set = (k: string, v: string | null) =>
    setParams(
      (p) => {
        if (v) p.set(k, v);
        else p.delete(k);
        return p;
      },
      { replace: k === 'id' ? false : true },
    );
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['withdrawals', tab, search],
    queryFn: () =>
      api<{ withdrawals: Row[]; counts: Record<string, number> }>(
        `/withdrawals?status=${TABS[tab].statuses}&limit=200${search ? `&q=${encodeURIComponent(search)}` : ''}`,
      ),
    refetchInterval: 15_000,
    placeholderData: (p) => p,
  });
  const c = data?.counts ?? {};
  const sum = (s: string) => s.split(',').reduce((t, k) => t + (c[k] ?? 0), 0);
  const total = data?.withdrawals.reduce((t, w) => t + w.amountMinor, 0) ?? 0;
  const open = (id: string) => set('id', id);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Withdrawals"
        description="Claude approves the clear cases and sends the rest here. Open one to see the card, the user, Claude's reasoning and the risk signals, then decide."
        actions={
          <SearchInput
            value={q}
            onChange={setQ}
            placeholder="User, email, wdr_ or order id"
            className="w-full sm:w-72"
          />
        }
      />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Tabs
          label="Withdrawal status"
          value={tab}
          onChange={(k) => set('tab', k === 'hold' ? null : k)}
          items={(Object.keys(TABS) as TabKey[]).map((k) => ({
            key: k,
            label: TABS[k].label,
            count:
              k === 'all' || k === 'paid' || k === 'closed' ? undefined : sum(TABS[k].statuses),
            hot: k === 'hold',
          }))}
        />
        {data && data.withdrawals.length > 0 && (
          <span className="text-[13px] text-stone tabular-nums sm:ml-auto">
            {fmtNum(data.withdrawals.length)} shown ·{' '}
            <b className="font-semibold text-ink">{fmtUsd(total / 100)}</b> total
          </span>
        )}
      </div>
      <ErrorNote error={error} onRetry={() => void refetch()} />

      {error && !data ? null : (
        <div className="card overflow-hidden">
          {isLoading ? (
            <SkeletonRows />
          ) : !data || data.withdrawals.length === 0 ? (
            tab === 'hold' && !search ? (
              <EmptyState icon={Check} title="Nothing on hold">
                Claude hasn't handed you any cash-outs. Held ones show up here.
              </EmptyState>
            ) : (
              <EmptyState
                title="No withdrawals match"
                action={
                  search ? (
                    <button className="btn-ghost px-4 py-2 text-[14px]" onClick={() => setQ('')}>
                      Clear search
                    </button>
                  ) : undefined
                }
              >
                {search ? `Nothing matches “${search}” in this tab.` : 'Nothing in this tab yet.'}
              </EmptyState>
            )
          ) : (
            <>
              {/* Phones, tablets and small laptops: a two-line list that never scrolls sideways. */}
              <ul className="divide-y divide-hairline xl:hidden">
                {data.withdrawals.map((w) => (
                  <ListRow key={w.id} w={w} selected={selected === w.id} onOpen={open} />
                ))}
              </ul>
              {/* Wide screens: the full table. */}
              <ScrollX className="hidden xl:block">
                <table className="data-table table-compact">
                  <thead>
                    <tr>
                      <th>Card</th>
                      <th>User</th>
                      <th className="text-right">Amount</th>
                      <th>Status</th>
                      <th>Risk</th>
                      <th>Claude</th>
                      <th className="text-right">Requested</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.withdrawals.map((w) => (
                      <tr
                        key={w.id}
                        onClick={() => open(w.id)}
                        className={`cursor-pointer ${selected === w.id ? '[&>td]:!bg-coin-tint/50' : ''}`}
                      >
                        <td>
                          <CardCell w={w} onOpen={open} />
                        </td>
                        <td className="max-w-[220px]">
                          <UserChip
                            id={w.userId}
                            username={w.username}
                            sub={w.email}
                            deleted={w.userStatus === 'deleted'}
                          />
                        </td>
                        <td>
                          <Amount coins={w.coins} minor={w.amountMinor} />
                        </td>
                        <td>
                          <StatusPill status={w.status} />
                        </td>
                        <td>
                          <RiskMeter score={w.riskScore} />
                        </td>
                        <td>
                          <ClaudeChip d={w.latestDecision} />
                        </td>
                        <td className="whitespace-nowrap text-right text-[13px] text-stone">
                          <TimeAgo t={w.createdAt} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollX>
            </>
          )}
        </div>
      )}
      {selected && <ReviewDrawer id={selected} onClose={() => set('id', null)} />}
    </div>
  );
}

/** Coins with the coin glyph (so the unit is never in doubt) over the USD value. */
function Amount({ coins, minor }: { coins: number; minor: number }) {
  return (
    <div className="whitespace-nowrap text-right tabular-nums">
      <div className="inline-flex items-center gap-1 text-[14px] font-bold">
        <CoinGlyph className="h-3.5 w-3.5" />
        {fmtNum(coins)}
        <span className="sr-only"> coins</span>
      </div>
      <div className="text-[12px] text-stone">{fmtUsd(minor / 100)}</div>
    </div>
  );
}

/** One queue entry below xl. The card name is the row's button (its ::after covers the row), so
 *  the whole row opens the review and keyboard users get one tab stop per row. */
function ListRow({
  w,
  selected,
  onOpen,
}: {
  w: Row;
  selected: boolean;
  onOpen: (id: string) => void;
}) {
  return (
    <li
      className={`relative grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-3 px-4 py-3.5 transition hover:bg-[#fbf8f3] sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_104px_96px] sm:px-5 ${selected ? '!bg-coin-tint/50' : ''}`}
    >
      <div className="min-w-0 sm:col-start-1 sm:row-start-1">
        <CardCell w={w} onOpen={onOpen} stretch />
      </div>
      <div className="sm:col-start-3 sm:row-start-1">
        <Amount coins={w.coins} minor={w.amountMinor} />
      </div>
      <div className="relative z-[1] min-w-0 self-center sm:col-start-2 sm:row-start-1">
        <UserChip
          id={w.userId}
          username={w.username}
          sub={w.email}
          deleted={w.userStatus === 'deleted'}
        />
      </div>
      <div className="flex items-center justify-end sm:col-start-4 sm:row-start-1 sm:items-start sm:pt-0.5">
        <StatusPill status={w.status} />
      </div>
      <div className="col-span-full flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-stone sm:row-start-2 sm:pl-[61px]">
        <span className="inline-flex items-center gap-2">
          <span className="max-sm:sr-only">Risk</span> <RiskMeter score={w.riskScore} />
        </span>
        {w.latestDecision ? (
          <ClaudeChip d={w.latestDecision} />
        ) : (
          <span className="text-[12px]">Not reviewed yet</span>
        )}
        <span className="ml-auto inline-flex items-center gap-1">
          <TimeAgo t={w.createdAt} />
          <ChevronRight size={14} aria-hidden="true" className="-mr-1 text-stone" />
        </span>
      </div>
    </li>
  );
}

function Thumb({ src, size = 40 }: { src: string | null | undefined; size?: number }) {
  return (
    <span
      className="grid shrink-0 place-items-center overflow-hidden rounded-[10px] border border-hairline bg-canvas"
      style={{ width: Math.round(size * 1.45), height: size }}
    >
      {src ? (
        <img src={src} alt="" className="h-full w-full object-cover" />
      ) : (
        <Gift size={Math.round(size * 0.42)} strokeWidth={1.75} className="text-stone" />
      )}
    </span>
  );
}

function CardCell({
  w,
  onOpen,
  stretch = false,
}: {
  w: Row;
  onOpen: (id: string) => void;
  /** Cover the whole (relative) row with the button's hit area. */
  stretch?: boolean;
}) {
  const item = w.item;
  return (
    <div className="flex min-w-0 items-center gap-3">
      <Thumb src={item?.imageUrl} size={34} />
      <div className="min-w-0">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(w.id);
          }}
          aria-label={`Review ${cardName(w)} for ${w.username}`}
          className={`block max-w-full truncate rounded-[4px] text-left text-[14px] font-bold ${
            stretch
              ? "after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-coin"
              : 'hover:underline hover:decoration-coin hover:decoration-2 hover:underline-offset-2'
          }`}
        >
          {item
            ? fmtMoney(item.amount, item.productCurrency ?? 'USD')
            : fmtUsd(w.amountMinor / 100)}
        </button>
        <div
          className="max-w-[200px] truncate text-[12px] text-stone"
          title={item?.productName ?? 'Gift card (old storefront)'}
        >
          {item?.productName ?? 'Gift card (old storefront)'}
        </div>
      </div>
    </div>
  );
}

const VERB: Record<string, { label: string; tone: 'mint' | 'coin' | 'berry' | 'neutral' }> = {
  approve: { label: 'Approve', tone: 'mint' },
  hold: { label: 'Hold', tone: 'coin' },
  reject: { label: 'Reject', tone: 'berry' },
};
const verbOf = (action: string) =>
  VERB[action.replace('_withdrawal', '')] ?? {
    label: action.charAt(0).toUpperCase() + action.slice(1).replace(/_/g, ' '),
    tone: 'neutral' as const,
  };

function ClaudeChip({ d }: { d: Decision | null }) {
  if (!d)
    return (
      <span className="text-[13px] text-stone" aria-label="No decision yet">
        —
      </span>
    );
  const v = verbOf(d.action);
  const isAi = d.actor.startsWith('ai:');
  return (
    <span className="inline-flex items-center gap-1.5" title={d.reasoning ?? ''}>
      <Pill tone={v.tone}>
        {isAi ? <Bot size={12} aria-hidden="true" /> : <Hand size={12} aria-hidden="true" />}{' '}
        {v.label}
        {d.confidence !== null && (
          <span className="font-semibold opacity-80">{Math.round(d.confidence * 100)}%</span>
        )}
      </Pill>
      {d.mode === 'shadow' && <span className="text-[12px] font-semibold text-stone">shadow</span>}
    </span>
  );
}

/* ------------------------------------------------------------------ Review drawer */

interface Detail {
  withdrawal: Row & {
    failureReason: string | null;
    externalRef: string | null;
    ip: string | null;
    country: string | null;
  };
  delivered: boolean;
  /** References from WizzGift; codes are never sent to staff. */
  delivery: {
    kind: 'code' | 'direct';
    deliveredAt: number | null;
    references: { label: string; value: string }[];
  } | null;
  user: {
    id: string;
    username: string;
    email: string | null;
    status: string;
    statusReason: string | null;
    createdAt: number;
    emailVerifiedAt: number | null;
    /** When the latest passed identity check was decided. */
    idApprovedAt?: number | null;
    signupCountry: string | null;
    countryCode: string | null;
  } | null;
  balance: { available: number; pending: number; withdrawing: number };
  events: {
    id: number;
    fromStatus: string | null;
    toStatus: string;
    actor: string;
    reason: string | null;
    createdAt: number;
  }[];
  decisions: Decision[];
  recipient: { label: string; value: string }[] | null;
  /** Shown masked: only staff who decide cash-outs see where the money goes. */
  recipientMasked?: boolean;
  risk: {
    score: number;
    decision: string;
    reasons: { code: string; weight: number; detail?: string }[];
    features: RiskFeatures;
  } | null;
  attempts: {
    id: string;
    status: string;
    externalRef: string | null;
    createdAt: number;
    httpStatus?: number;
    code?: string;
    message?: string;
  }[];
  staffNames: Record<string, string>;
}

const APPROVE_REASONS = ['Earnings look genuine', 'Known good user', 'Checked the network'];
const HOLD_REASONS = ['Waiting for offers to clear', 'Needs more history', 'Asked the network'];
const REJECT_REASONS = [
  'Multiple accounts',
  'Earnings reversed by the network',
  'Payout destination shared',
  'Suspicious activity',
];
const USER_MESSAGES = [
  'We couldn’t approve this cash-out. Your coins are back in your balance; contact support if you have questions.',
  'Some of the offers behind this balance were reversed by the partner, so we couldn’t send this card.',
  'This account doesn’t meet our one-account-per-person rule.',
];

function ReviewDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const can = useCan();
  const { data, error, refetch } = useQuery({
    queryKey: ['withdrawal', id],
    queryFn: () => api<Detail>(`/withdrawals/${id}`),
    refetchInterval: 10_000,
  });
  useAssistantContext({ userId: data?.user?.id });
  const w = data?.withdrawal;
  const names = data?.staffNames ?? {};
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['withdrawals'] }),
      qc.invalidateQueries({ queryKey: ['withdrawal', id] }),
      qc.invalidateQueries({ queryKey: ['overview'] }),
    ]);

  const decide = async (action: 'approve' | 'hold' | 'reject') => {
    if (!w) return;
    const what = cardName(w);
    const who = data?.user?.username ?? 'the user';
    const reason = await confirmAction(
      action === 'approve'
        ? {
            title: `Approve and send ${what}?`,
            body: (
              <>
                This places the order with WizzGift right away. It can't be undone.
                <dl className="mt-3 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 rounded-[12px] bg-canvas px-3.5 py-3 text-[13px] text-ink">
                  <dt className="text-stone">{who} pays</dt>
                  <dd className="font-bold tabular-nums">{fmtNum(w.coins)} coins</dd>
                  <dt className="text-stone">We pay WizzGift</dt>
                  <dd className="font-bold tabular-nums">about {fmtUsd(w.amountMinor / 100)}</dd>
                  {data?.recipient?.map((r) => (
                    <Fragment key={r.label}>
                      <dt className="text-stone">{r.label}</dt>
                      <dd className="break-all font-bold">{r.value}</dd>
                    </Fragment>
                  ))}
                </dl>
              </>
            ),
            confirmLabel: 'Approve & send',
            tone: 'coin',
            reason: { label: 'Why (internal, kept in the audit log)', presets: APPROVE_REASONS },
          }
        : action === 'hold'
          ? {
              title: 'Put this cash-out on hold?',
              body: 'It stays reserved and waits here until you approve or reject it. The user sees “Reviewing”.',
              confirmLabel: 'Put on hold',
              reason: { label: 'Why (internal)', presets: HOLD_REASONS },
            }
          : {
              eyebrow: 'Reject · step 1 of 2',
              title: 'Reject this cash-out?',
              body: (
                <>
                  <b className="tabular-nums text-ink">{fmtNum(w.coins)} coins</b> go back to {who}
                  's balance. Next, you'll write what they read.
                </>
              ),
              confirmLabel: 'Next: message to user',
              reason: { label: 'Why (internal, never shown to the user)', presets: REJECT_REASONS },
            },
    );
    if (reason === null) return;
    let userMessage: string | undefined;
    if (action === 'reject') {
      const m = await confirmAction({
        eyebrow: 'Reject · step 2 of 2',
        title: `What should ${who} read?`,
        body: 'Shown on their cash-out and in the email. Keep it short; no internal details.',
        confirmLabel: `Reject & refund ${fmtNum(w.coins)} coins`,
        tone: 'danger',
        reason: {
          label: 'Message to the user',
          presets: USER_MESSAGES,
          initial: USER_MESSAGES[0]!,
        },
      });
      if (m === null) return;
      userMessage = m;
    }
    await run(
      () => api(`/withdrawals/${id}/decision`, { body: { action, reason, userMessage } }),
      action === 'approve'
        ? 'Approved. The order is being placed.'
        : action === 'hold'
          ? 'Put on hold.'
          : 'Rejected. Coins returned.',
    );
    await refresh();
  };
  const rereview = async () => {
    await run(() => api(`/withdrawals/${id}/rereview`, { body: {} }), 'Sent back to Claude.');
    await refresh();
  };

  const open = w && OPEN.includes(w.status);
  const ai = data?.decisions.find((d) => d.actor.startsWith('ai:'));
  return (
    <Drawer
      onClose={onClose}
      title={w ? cardName(w) : 'Withdrawal'}
      subtitle={
        w && (
          <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
            <Mono>{w.id}</Mono>
            <span className="whitespace-nowrap">
              Requested <TimeAgo t={w.createdAt} />
            </span>
          </span>
        )
      }
      headerRight={w && <StatusPill status={w.status} />}
      footer={
        w &&
        (open && can('withdrawals.decide') ? (
          <div
            className={`grid gap-2 whitespace-nowrap sm:flex sm:flex-wrap sm:items-center ${
              w.status === 'on_hold' ? 'grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]' : 'grid-cols-2'
            }`}
          >
            <button className="btn-coin col-span-2" onClick={() => decide('approve')}>
              <Check size={16} aria-hidden="true" /> Approve & send
            </button>
            {w.status !== 'on_hold' && (
              <button className="btn-ghost" onClick={() => decide('hold')}>
                <Hand size={16} aria-hidden="true" /> Hold
              </button>
            )}
            <button
              className="btn-ghost text-berry-deep hover:border-berry-deep"
              onClick={() => decide('reject')}
            >
              <X size={16} aria-hidden="true" /> Reject
            </button>
            {w.status === 'on_hold' && (
              <button className="btn-ghost sm:ml-auto" onClick={rereview}>
                <RotateCcw size={15} aria-hidden="true" /> Ask Claude again
              </button>
            )}
          </div>
        ) : open ? (
          <ViewOnly>Deciding on cash-outs isn’t part of your roles.</ViewOnly>
        ) : (
          <FooterState w={w} />
        ))
      }
    >
      {!data || !w ? (
        error ? (
          <ErrorNote error={error} onRetry={() => void refetch()} />
        ) : (
          <div className="space-y-4" role="status" aria-label="Loading">
            {[128, 196, 240].map((h) => (
              <div key={h} className="card animate-pulse" style={{ height: h }} />
            ))}
          </div>
        )
      ) : (
        <div className="space-y-4">
          <ErrorNote error={error} />
          {/* What and how much */}
          <Card pad={false}>
            <div className="flex gap-4 p-4 sm:p-5">
              <span className="hidden sm:block">
                <Thumb src={w.item?.imageUrl} size={64} />
              </span>
              <div className="grid flex-1 grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-3">
                <Fact label="User pays" value={fmtNum(w.coins)} unit="coins" accent />
                <Fact label="Our cost (est.)" value={fmtUsd(w.amountMinor / 100)} />
                {w.costMinor !== null ? (
                  <Margin coins={w.coins} costMinor={w.costMinor} />
                ) : (
                  <Fact label="Country" value={w.country ?? '—'} />
                )}
              </div>
            </div>
            {data.recipient && (
              <div className="border-t border-hairline bg-coin-tint/40 px-4 py-3.5 sm:px-5">
                <div className="flex items-center gap-2 text-[13px] font-bold text-coin-ink">
                  <AlertTriangle size={14} aria-hidden="true" /> Pays out to an account the user
                  typed
                </div>
                <dl className="mt-2 space-y-2 sm:space-y-1">
                  {data.recipient.map((r) => (
                    <div
                      key={r.label}
                      className="grid gap-x-3 text-[14px] sm:grid-cols-[minmax(96px,auto)_minmax(0,1fr)] sm:items-center"
                    >
                      <dt className="text-[13px] text-stone sm:text-[14px]">{r.label}</dt>
                      <dd className="flex min-w-0 items-center gap-1">
                        <span className="min-w-0 break-all font-bold">{r.value}</span>
                        {!data.recipientMasked && <CopyButton text={r.value} />}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </Card>

          {/* Who */}
          {data.user && <UserCard d={data} />}

          {/* Claude */}
          <ClaudeCard decisions={data.decisions} features={data.risk?.features} names={names} />

          {/* Risk */}
          {data.risk && <RiskCard risk={data.risk} />}

          {/* Delivery */}
          {(w.externalRef || data.attempts.length > 0 || w.failureReason) && (
            <Card title="Delivery">
              <KV
                items={[
                  ...(w.externalRef
                    ? ([['WizzGift order', <Mono key="r">{w.externalRef}</Mono>]] as [
                        ReactNode,
                        ReactNode,
                      ][])
                    : []),
                  [
                    'Delivered',
                    data.delivery
                      ? data.delivery.kind === 'code'
                        ? 'Code delivered (stored encrypted)'
                        : 'Sent straight to the account (no code)'
                      : 'Not yet',
                  ],
                  ...(data.delivery?.deliveredAt
                    ? ([['Delivered at', new Date(data.delivery.deliveredAt).toLocaleString()]] as [
                        ReactNode,
                        ReactNode,
                      ][])
                    : []),
                  ...((data.delivery?.references ?? []).map((r) => [
                    r.label,
                    <Mono key={r.value}>{r.value}</Mono>,
                  ]) as [ReactNode, ReactNode][]),
                  ...(w.failureReason
                    ? ([
                        [
                          'Told the user',
                          <span key="f" className="text-berry-deep">
                            {w.failureReason}
                          </span>,
                        ],
                      ] as [ReactNode, ReactNode][])
                    : []),
                ]}
              />
              {data.attempts.length > 0 && (
                <ul className="mt-4 divide-y divide-hairline border-t border-hairline text-[13px]">
                  {data.attempts.map((a) => (
                    <li key={a.id} className="flex items-start gap-3 py-2.5">
                      <Truck
                        size={14}
                        className="mt-[3px] shrink-0 text-stone"
                        aria-hidden="true"
                      />
                      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-semibold">Order attempt</span>
                        <StatusPill status={a.status} />
                        {a.httpStatus !== undefined && (
                          <span className="text-stone">HTTP {a.httpStatus}</span>
                        )}
                        {a.code && <code className="text-[12px] text-stone">{a.code}</code>}
                        {a.message && <span className="basis-full text-graphite">{a.message}</span>}
                      </div>
                      <span className="shrink-0 text-[12px] text-stone">
                        <TimeAgo t={a.createdAt} />
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}

          {/* History */}
          <Card title="History">
            <ol className="relative ml-1.5 space-y-4 border-l-2 border-hairline pl-5">
              {data.events.map((e) => (
                <li key={e.id} className="relative">
                  <span
                    aria-hidden="true"
                    className="absolute -left-[28px] top-[3px] h-3.5 w-3.5 rounded-full border-2 border-paper bg-ink"
                  />
                  <div className="flex items-start gap-3">
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 text-[14px]">
                      {e.fromStatus && (
                        <>
                          <StatusPill status={e.fromStatus} />
                          <span className="text-stone" aria-label="to">
                            →
                          </span>
                        </>
                      )}
                      <StatusPill status={e.toStatus} />
                      <span className="font-semibold">{actorLabel(e.actor, names)}</span>
                    </div>
                    <span className="shrink-0 pt-px text-[12px] text-stone">
                      <TimeAgo t={e.createdAt} />
                    </span>
                  </div>
                  {e.reason && (
                    <p className="mt-1 text-[13px] leading-relaxed text-graphite">{e.reason}</p>
                  )}
                </li>
              ))}
            </ol>
          </Card>
          {ai && ai.mode === 'shadow' && (
            <p className="px-2 text-center text-[12px] text-stone">
              Claude runs in shadow mode: it recommends, staff decide. Change it in Settings.
            </p>
          )}
        </div>
      )}
    </Drawer>
  );
}

function Fact({
  label,
  value,
  unit,
  sub,
  subTone = '',
  accent,
}: {
  label: string;
  value: string;
  unit?: string;
  sub?: string | undefined;
  subTone?: string;
  accent?: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="text-[12px] font-semibold text-stone">{label}</div>
      <div
        className={`mt-1 text-[20px] font-extrabold leading-tight tracking-[-0.01em] tabular-nums ${accent ? 'text-coin-deep' : ''}`}
      >
        {value}
        {unit && (
          <span className="ml-1 text-[13px] font-semibold tracking-normal text-stone">{unit}</span>
        )}
      </div>
      {sub && <div className={`mt-0.5 text-[12px] ${subTone || 'text-stone'}`}>{sub}</div>}
    </div>
  );
}

/** What WizzGift charged, with the margin against the coins' face value (1,000 coins = $1). */
function Margin({ coins, costMinor }: { coins: number; costMinor: number }) {
  const m = coins / 1000 - costMinor / 100;
  return (
    <Fact
      label="WizzGift charged"
      value={fmtUsd(costMinor / 100)}
      sub={`Margin ${m < 0 ? '−' : ''}${fmtUsd(Math.abs(m))}`}
      subTone={m < 0 ? 'font-semibold text-berry-deep' : ''}
    />
  );
}

function FooterState({ w }: { w: Detail['withdrawal'] }) {
  const states: Record<string, { text: string; icon: typeof Check; cls: string }> = {
    approved: {
      text: 'Approved. The order is queued for WizzGift.',
      icon: Check,
      cls: 'bg-sky-500/12 text-sky-800',
    },
    processing: {
      text: 'Order placed. Waiting for WizzGift to deliver; this settles on its own.',
      icon: Truck,
      cls: 'bg-sky-500/12 text-sky-800',
    },
    paid: {
      text: 'Delivered and paid. Nothing left to do.',
      icon: Check,
      cls: 'bg-mint/15 text-mint-deep',
    },
    rejected: {
      text: 'Rejected. The coins went back to the user.',
      icon: X,
      cls: 'bg-berry/10 text-berry-deep',
    },
    failed: {
      text: 'Failed. The coins went back to the user.',
      icon: X,
      cls: 'bg-berry/10 text-berry-deep',
    },
    refunded: { text: 'Refunded.', icon: RotateCcw, cls: 'bg-ink/[0.06] text-graphite' },
  };
  const s = states[w.status] ?? {
    text: w.status,
    icon: Check,
    cls: 'bg-ink/[0.06] text-graphite',
  };
  return (
    <div className="flex min-h-10 items-center gap-3 text-[14px] text-graphite">
      <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${s.cls}`}>
        <s.icon size={15} aria-hidden="true" />
      </span>
      {s.text}
    </div>
  );
}

function UserCard({ d }: { d: Detail }) {
  const u = d.user!;
  const ageDays = Math.floor((Date.now() - u.createdAt) / 86_400_000);
  const from = u.signupCountry;
  const now = d.withdrawal.country;
  return (
    <Card
      title="User"
      actions={
        <Link
          to={`/users/${u.id}`}
          className="py-1 text-[13px] font-semibold underline decoration-coin decoration-2 underline-offset-4"
        >
          Open profile
        </Link>
      }
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <UserChip id={u.id} username={u.username} sub={u.email} deleted={u.status === 'deleted'} />
        <span className="flex flex-wrap gap-1.5">
          <StatusPill status={u.status} />
          {u.status !== 'deleted' &&
            (u.emailVerifiedAt ? (
              <Pill tone="mint">Email verified</Pill>
            ) : (
              <Pill tone="berry">Email unverified</Pill>
            ))}
          {u.status !== 'deleted' &&
            (u.idApprovedAt && u.idApprovedAt > Date.now() - ID_CHECK_VALID_MS ? (
              <Pill tone="mint">
                <ShieldCheck size={12} aria-hidden="true" /> ID verified
              </Pill>
            ) : (
              <Pill tone="neutral">{u.idApprovedAt ? 'ID check expired' : 'ID not verified'}</Pill>
            ))}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-4 border-t border-hairline pt-4 sm:grid-cols-4">
        <Fact
          label="Account age"
          value={ageDays < 1 ? 'Today' : `${ageDays} day${ageDays === 1 ? '' : 's'}`}
        />
        <Fact
          label="Country"
          value={from && now && from !== now ? `${from} → ${now}` : (from ?? now ?? '—')}
          sub={
            from && now
              ? from === now
                ? 'Signup and cash-out'
                : 'Signup → cash-out'
              : from
                ? 'At signup'
                : undefined
          }
        />
        <Fact label="Available" value={fmtNum(d.balance.available)} unit="coins" />
        <Fact label="Pending" value={fmtNum(d.balance.pending)} unit="coins" />
      </div>
      {u.statusReason && u.status !== 'active' && (
        <p className="mt-3 text-[13px] text-graphite">Status note: {u.statusReason}</p>
      )}
    </Card>
  );
}

function ClaudeCard({
  decisions,
  features,
  names,
}: {
  decisions: Decision[];
  features: RiskFeatures | undefined;
  names: Record<string, string>;
}) {
  const [showAll, setShowAll] = useState(false);
  if (decisions.length === 0)
    return (
      <Card title="Decisions">
        <p className="text-[14px] text-stone">
          No decision yet. Claude reviews new requests within a minute.
        </p>
      </Card>
    );
  const [latest, ...older] = decisions;
  return (
    <Card title="Decisions">
      <DecisionView d={latest!} features={features} names={names} />
      {older.length > 0 && (
        <div className="mt-4 border-t border-hairline pt-3">
          <button
            type="button"
            aria-expanded={showAll}
            className="py-1 text-[13px] font-semibold text-graphite underline underline-offset-2 hover:text-ink"
            onClick={() => setShowAll((s) => !s)}
          >
            {showAll ? 'Hide' : 'Show'} {older.length} earlier decision
            {older.length === 1 ? '' : 's'}
          </button>
          {showAll && (
            <div className="mt-3 space-y-5">
              {older.map((d) => (
                <DecisionView key={d.id} d={d} features={features} names={names} />
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function DecisionView({
  d,
  features,
  names,
}: {
  d: Decision;
  features: RiskFeatures | undefined;
  names: Record<string, string>;
}) {
  const v = verbOf(d.action);
  const isAi = d.actor.startsWith('ai:');
  return (
    <div>
      <div className="flex items-start gap-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-canvas">
          {isAi ? <Bot size={16} aria-hidden="true" /> : <Hand size={16} aria-hidden="true" />}
        </span>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 pt-1">
          <span className="font-bold">{actorLabel(d.actor, names)}</span>
          <Pill tone={v.tone}>{v.label}</Pill>
          {d.mode === 'shadow' && <Pill>Shadow, not applied</Pill>}
          {d.outcome && <Pill tone="sky">Staff {d.outcome.replace('staff_', '')}</Pill>}
        </div>
        <span className="shrink-0 pt-1.5 text-[12px] text-stone">
          <TimeAgo t={d.createdAt} />
        </span>
      </div>
      {d.confidence !== null && (
        <div className="mt-3 flex items-center gap-3 text-[13px]">
          <span className="text-stone">Confidence</span>
          <span
            className="h-1.5 w-28 overflow-hidden rounded-full bg-ink/10 sm:w-40"
            aria-hidden="true"
          >
            <span
              className="block h-full bg-ink"
              style={{ width: `${Math.round(d.confidence * 100)}%` }}
            />
          </span>
          <b className="tabular-nums">{Math.round(d.confidence * 100)}%</b>
        </div>
      )}
      {d.reasoning && <p className="mt-3 text-[14px] leading-relaxed">{d.reasoning}</p>}
      {d.evidence && d.evidence.length > 0 && (
        <ul className="mt-3 grid gap-1.5" aria-label="Evidence">
          {d.evidence.map((e, i) => {
            const r = describeRef(features, e.ref);
            return (
              <li
                key={i}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-[10px] bg-canvas px-3 py-2 text-[13px]"
              >
                <span className="text-graphite">{r.label}</span>
                {r.value !== null && <span className="font-bold tabular-nums">{r.value}</span>}
                {e.note && <span className="text-graphite">· {e.note}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** The signals that matter most when reviewing, in plain words, worst first. */
const KEY_SIGNALS: {
  path: string;
  bad: (f: RiskFeatures) => boolean;
  /** Shown only when this is true (signals older reviews don't have). */
  when?: (f: RiskFeatures) => boolean;
}[] = [
  { path: 'account.ageDays', bad: (f) => f.account.ageDays < 2 },
  {
    path: 'earnings.lifetimeCoins',
    // Offers plus the old account, staff credits and the site's own bonuses: what the account earned.
    bad: (f) =>
      f.withdrawal.coins >
      f.earnings.lifetimeCoins +
        (f.earnings.oldAccountCoins ?? 0) +
        (f.earnings.staffCreditCoins ?? 0) +
        (f.earnings.bonusCoins ?? 0),
  },
  { path: 'earnings.bonusCoins', bad: () => false, when: (f) => (f.earnings.bonusCoins ?? 0) > 0 },
  { path: 'earnings.coins7d', bad: () => false },
  { path: 'earnings.reversalRatio', bad: (f) => f.earnings.reversalRatio > 0.2 },
  { path: 'earnings.conversionsWithoutClick', bad: (f) => f.earnings.conversionsWithoutClick > 0 },
  { path: 'network.otherUsersOnSameIps', bad: (f) => f.network.otherUsersOnSameIps > 0 },
  { path: 'network.otherUsersOnSameDevice', bad: (f) => f.network.otherUsersOnSameDevice > 0 },
  {
    path: 'app.otherUsersOnSameAndroidId',
    bad: (f) => (f.app?.otherUsersOnSameAndroidId ?? 0) > 0,
    when: (f) => (f.app?.appInstalls ?? 0) > 0,
  },
  {
    path: 'app.otherUsersOnSameAdvertisingId',
    bad: (f) => (f.app?.otherUsersOnSameAdvertisingId ?? 0) > 0,
    when: (f) => (f.app?.appInstalls ?? 0) > 0,
  },
  {
    path: 'withdrawal.destinationReuseOtherUsers',
    bad: (f) => f.withdrawal.destinationReuseOtherUsers > 0,
  },
  { path: 'network.distinctCountries30d', bad: (f) => f.network.distinctCountries30d.length >= 3 },
  {
    path: 'network.signupBotScore',
    bad: (f) => f.network.signupBotScore !== null && f.network.signupBotScore < 30,
  },
  { path: 'history.paid', bad: () => false },
  { path: 'history.rejected', bad: (f) => f.history.rejected > 0 },
];

function RiskCard({ risk }: { risk: NonNullable<Detail['risk']> }) {
  const f = risk.features;
  const verdict: Record<string, string> = {
    allow: 'Low risk',
    review: 'Worth a look',
    hold: 'Hold',
    block: 'Block',
  };
  return (
    <Card
      title="Risk signals"
      actions={
        <span className="inline-flex items-center gap-2.5 text-[13px]">
          <span className="font-semibold text-graphite">
            {verdict[risk.decision] ?? risk.decision}
          </span>
          <RiskMeter score={risk.score} />
        </span>
      }
    >
      {risk.reasons.length === 0 ? (
        <p className="flex items-center gap-2 text-[14px] text-mint-deep">
          <Check size={16} aria-hidden="true" /> No risk rule fired.
        </p>
      ) : (
        <ul className="space-y-2.5" aria-label="Rules that fired">
          {risk.reasons.map((r, i) => {
            const high = r.weight >= 30;
            return (
              <li key={i} className="flex items-start gap-3 text-[14px]">
                <ShieldAlert
                  size={15}
                  aria-hidden="true"
                  className={`mt-[3px] shrink-0 ${high ? 'text-berry-deep' : 'text-coin-ink'}`}
                />
                <div className="min-w-0 flex-1">
                  <span className="font-semibold">{ruleLabel(r.code)}</span>
                  {r.detail && <span className="block text-[13px] text-stone">{r.detail}</span>}
                </div>
                <span className="inline-flex shrink-0 items-center gap-2 pt-[3px]">
                  <span
                    className="hidden h-1.5 w-16 overflow-hidden rounded-full bg-ink/10 sm:block"
                    aria-hidden="true"
                  >
                    <span
                      className={`block h-full ${high ? 'bg-berry' : 'bg-coin'}`}
                      style={{ width: `${Math.min(100, r.weight * 1.6)}%` }}
                    />
                  </span>
                  <span
                    className="w-8 text-right text-[12px] font-bold tabular-nums"
                    aria-label={`adds ${r.weight} points`}
                  >
                    +{r.weight}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {f && (
        <dl className="mt-5 grid grid-cols-1 gap-x-8 border-t border-hairline pt-2 sm:grid-cols-2">
          {KEY_SIGNALS.filter((s) => !s.when || s.when(f)).map((s) => {
            const r = describeRef(f, s.path);
            const bad = s.bad(f);
            return (
              <div
                key={s.path}
                className="flex items-baseline justify-between gap-3 border-b border-hairline/70 py-2 text-[13px]"
              >
                <dt className="text-graphite">{FEATURES[s.path]?.label ?? s.path}</dt>
                <dd className={`text-right font-bold tabular-nums ${bad ? 'text-berry-deep' : ''}`}>
                  {bad && <span className="sr-only">Flagged: </span>}
                  {r.value ?? '—'}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
      {f && (
        <details className="group mt-4">
          <summary className="inline-flex cursor-pointer list-none items-center gap-1 py-1 text-[13px] font-semibold text-graphite hover:text-ink [&::-webkit-details-marker]:hidden">
            <ChevronRight
              size={14}
              aria-hidden="true"
              className="transition group-open:rotate-90"
            />
            All data Claude saw
          </summary>
          <pre className="mt-2 max-h-80 overflow-auto rounded-[10px] bg-canvas p-3 text-[12px] leading-relaxed">
            {JSON.stringify(f, null, 2)}
          </pre>
        </details>
      )}
    </Card>
  );
}
